import React, { useEffect, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Analysis = () => {
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        setAnimate(true);
    }, []);

    const [view, setView] = useState('forecast'); // forecast, dispatches, production, materials

    useEffect(() => {
        setAnimate(true);
        const timer = setInterval(() => {
            setView(prev => {
                const views = ['forecast', 'dispatches', 'production', 'materials'];
                const nextIdx = (views.indexOf(prev) + 1) % views.length;
                return views[nextIdx];
            });
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const predictionData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
        datasets: [
            {
                label: 'Historical',
                data: [450, 480, 420, 510, 490, null, null, null],
                borderColor: '#4f46e5',
                backgroundColor: 'rgba(79, 70, 229, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 4,
            },
            {
                label: 'Forecast',
                data: [null, null, null, null, 490, 550, 580, 620],
                borderColor: '#0ea5e9',
                borderDash: [5, 5],
                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 6,
                pointBackgroundColor: '#0ea5e9',
            }
        ],
    };

    const dispatchData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
        datasets: [{
            label: 'Dispatches',
            data: [320, 380, 350, 440, 510],
            backgroundColor: 'rgba(79, 70, 229, 0.8)',
            borderRadius: 12,
            hoverBackgroundColor: '#4f46e5',
        }]
    };

    const productionData = {
        labels: ['W1', 'W2', 'W3', 'W4'],
        datasets: [{
            label: 'Yield',
            data: [98.2, 99.1, 97.8, 99.5],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
        }]
    };

    const materialsData = {
        labels: ['PVA', 'Resin', 'Demin Water', 'AM1512'],
        datasets: [{
            data: [45, 25, 20, 10],
            backgroundColor: [
                '#4f46e5',
                '#0ea5e9',
                '#8b5cf6',
                '#f43f5e'
            ],
            borderWidth: 0,
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#0f172a',
                padding: 12,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 12 },
                cornerRadius: 8,
            }
        },
        scales: {
            y: {
                beginAtZero: false,
                grid: { color: 'rgba(0,0,0,0.05)' },
                ticks: { font: { size: 10, weight: 'bold' }, color: '#64748b' }
            },
            x: {
                grid: { display: false },
                ticks: { font: { size: 10, weight: 'bold' }, color: '#64748b' }
            }
        }
    };

    const insights = [
        {
            title: "Predictive Forecasting",
            desc: "AI-driven stock models predict future demand with 98% accuracy based on historical batch lineage.",
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            )
        },
        {
            title: "Variance Detection",
            desc: "Instantly identify deviations in production yield before they affect your bottom line.",
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            )
        },
        {
            title: "Automated Compliance",
            desc: "Generate end-to-end data insights for regulatory audits in a single click.",
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            )
        }
    ];

    return (
        <section id="analysis" className="py-8 bg-slate-50 overflow-hidden">
            <div className="section-padding">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className={`transition-all duration-1000 ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
                            </span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Predictive Engine Active</span>
                        </div>

                        <h2 className="heading-lg mb-6 leading-tight">
                            Beyond Tracking: <br />
                            <span className="gradient-text font-black">Predictive Insights</span>
                        </h2>

                        <p className="text-p mb-10 text-slate-600 max-w-xl">
                            Our software goes beyond simple record-keeping. Using advanced batch lineage and real-time sensor data, OmniTrace predicts future stock levels and identifies production bottlenecks before they occur.
                        </p>

                        <div className="space-y-6">
                            {insights.map((item, i) => (
                                <div key={i} className="flex gap-4 group cursor-default">
                                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-indigo-600 to-sky-500 text-white border-transparent shadow-lg shadow-indigo-200 flex items-center justify-center transition-all duration-300">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-sky-600 transition-colors">{item.title}</h4>
                                        <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`relative transition-all duration-1000 delay-300 ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                        {/* Dashboard Preview Mockup */}
                        <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] border border-slate-200 shadow-2xl p-4 sm:p-8 relative z-10 overflow-hidden min-h-[450px] sm:min-h-[500px] flex flex-col">
                            <div className="flex justify-between items-start mb-6 sm:mb-8">
                                <div className="animate-fade-in" key={view}>
                                    <div className="text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                                        {view === 'forecast' ? 'Inventory Forecast' :
                                            view === 'dispatches' ? 'Dispatch Volume' :
                                                view === 'production' ? 'Production Efficiency' : 'Material Utilization'}
                                    </div>
                                    <div className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">
                                        {view === 'forecast' ? 'Availability Trends' :
                                            view === 'dispatches' ? 'Monthly Shipments' :
                                                view === 'production' ? 'Batch Yield Score' : 'Resource Breakdown'}
                                        <span className="text-sky-500 italic ml-2 block sm:inline text-sm sm:text-base">
                                            {view === 'forecast' ? '+24%' :
                                                view === 'dispatches' ? '+18%' :
                                                    view === 'production' ? '99.5%' : 'Top 4'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    {['forecast', 'dispatches', 'production', 'materials'].map(v => (
                                        <div key={v} className={`h-1 rounded-full transition-all duration-500 ${view === v ? 'bg-sky-500 w-6' : 'bg-slate-100 w-2'}`} />
                                    ))}
                                </div>
                            </div>

                            <div className="flex-grow relative h-64 lg:h-72">
                                <div className="absolute inset-0 animate-fade-in" key={view}>
                                    {view === 'forecast' && <Line data={predictionData} options={chartOptions} />}
                                    {view === 'dispatches' && <Bar data={dispatchData} options={chartOptions} />}
                                    {view === 'production' && <Line data={productionData} options={chartOptions} />}
                                    {view === 'materials' && (
                                        <div className="h-full flex items-center justify-center">
                                            <div className="w-full max-w-[280px] sm:max-w-none sm:w-72 lg:w-96">
                                                <Doughnut
                                                    data={materialsData}
                                                    options={{
                                                        ...chartOptions,
                                                        cutout: '50%',
                                                        scales: {
                                                            x: { display: false },
                                                            y: { display: false }
                                                        },
                                                        plugins: {
                                                            ...chartOptions.plugins,
                                                            legend: {
                                                                display: true,
                                                                position: 'bottom',
                                                                labels: {
                                                                    boxWidth: 12,
                                                                    padding: 20,
                                                                    font: { size: 11, weight: 'bold' },
                                                                    color: '#475569'
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6 sm:mt-8">
                                <div className="bg-slate-50 p-3 sm:p-4 rounded-2xl border border-slate-100 transition-all hover:border-sky-200">
                                    <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                                        {view === 'materials' ? 'Primary Item' : 'Target Threshold'}
                                    </div>
                                    <div className="text-lg sm:text-xl font-black text-slate-900">
                                        {view === 'forecast' ? '1,240' :
                                            view === 'dispatches' ? '600' :
                                                view === 'production' ? '98.5%' : 'PVA (45%)'}
                                        <span className="text-[9px] text-slate-400 font-bold ml-1 uppercase block sm:inline">
                                            {view === 'forecast' ? 'Units' :
                                                view === 'dispatches' ? 'Orders' :
                                                    view === 'production' ? 'Target' : 'Peak'}
                                        </span>
                                    </div>
                                </div>
                                <div className="bg-slate-50 p-3 sm:p-4 rounded-2xl border border-slate-100 transition-all hover:border-indigo-200">
                                    <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Confidence Score</div>
                                    <div className="text-lg sm:text-xl font-black text-emerald-600">98.2%</div>
                                </div>
                            </div>
                        </div>

                        {/* Decorative background elements */}
                        <div className="absolute -top-10 -right-10 w-64 h-64 bg-indigo-100 rounded-full blur-[80px] -z-10 opacity-60"></div>
                        <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-sky-100 rounded-full blur-[80px] -z-10 opacity-60"></div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Analysis;
